import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Users,
  Search,
  Filter,
  Plus,
  Trash2,
  Edit,
  Send,
  Eye,
  EyeOff,
  UserPlus,
  Shuffle,
  CheckSquare,
  Square,
  GripVertical,
  X,
  ChevronDown,
  ChevronUp,
  DollarSign,
  AlertCircle,
  Scale,
  Calendar,
  UserCircle,
  Trophy,
  Loader2,
  Link2,
  Upload,
  RefreshCw,
  CheckCircle,
  ExternalLink,
  GraduationCap,
  Clock,
  Lock,
  Unlock,
  List,
  LayoutGrid,
  Undo2,
  Settings,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import TeamStaffManager from "./TeamStaffManager";
import RosterAvatar from "./RosterAvatar";
import "./teamRosterLayout.css";

// Squadline Logo URL
const SQUADLINE_LOGO = "https://customer-assets.emergentagent.com/job_c9359842-80d8-4acf-8a11-6884ae51c080/artifacts/7gldz4nd_Squadline%20logo.png";
const createIdempotencyKey = (prefix = "roster") =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

// Available staff roles
const STAFF_ROLES = [
  { value: "head_coach", label: "Head Coach" },
  { value: "assistant_coach", label: "Assistant Coach" },
  { value: "team_manager", label: "Team Manager" },
  { value: "director", label: "Director" },
  { value: "coordinator", label: "Coordinator" },
  { value: "trainer", label: "Trainer" },
];

const staffKey = (person) => JSON.stringify([person.staff_type, person.staff_id]);

const DraggableStaff = ({ person, selected, onSelect, disabled, teamId }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `staff-${staffKey(person)}`, data: { person, type: "staff", teamId }, disabled,
  });
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim() || "Name unavailable";
  return <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform) }} className="roster-staff-candidate">
    <Checkbox checked={selected} onCheckedChange={onSelect} disabled={disabled} aria-label={`Select ${name}`} />
    <button type="button" {...attributes} {...listeners} className="roster-drag-handle" disabled={disabled} aria-label={`Drag ${name}`}><GripVertical className="h-4 w-4" /></button>
    <div className="roster-photo-identity"><RosterAvatar src={person.avatar_url} firstName={person.first_name} lastName={person.last_name} /><div><strong>{name}</strong><p>{person.staff_type === "coach" ? "Coach" : person.staff_type === "user" ? "Organization user" : "Staff"}</p><p>{person.email}</p></div></div>
  </div>;
};

// Drag Overlay Staff Card
const DragOverlayStaff = ({ coach }) => {
  if (!coach) return null;
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-400 bg-blue-50 shadow-lg cursor-grabbing">
      <RosterAvatar src={coach.avatar_url} firstName={coach.first_name} lastName={coach.last_name} />
      <p className="text-sm font-medium text-slate-900">
        {coach.first_name} {coach.last_name}
      </p>
    </div>
  );
};

// Draggable Player Card Component
const DraggablePlayer = ({ player, registration, isSelected, onSelect, isDragging, isPending, onToggleLock, onEditConstraints }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: registration.registration_id,
    data: { registration, player },
    disabled: Boolean(registration.roster_locked),
  });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.3 : 1,
        transition: 'opacity 0.15s ease',
      }
    : {
        transition: 'all 0.2s ease',
      };

  const isPaid = registration.payment_status === "paid";
  const age = player?.date_of_birth
    ? Math.floor((new Date() - new Date(player.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`program-builder-player group flex items-center gap-2 p-2 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
        isPending
          ? "bg-amber-50 border-amber-300 opacity-70"
          : isSelected
          ? "bg-indigo-50 border-indigo-300"
          : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
      }`}
      data-testid={`player-card-${registration.registration_id}`}
    >
      {/* Selection Checkbox */}
      <div>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect(registration.registration_id)}
          disabled={Boolean(registration.roster_locked)}
          aria-label={`Select ${player?.first_name || ""} ${player?.last_name || ""}`}
          className="data-[state=checked]:bg-indigo-600"
        />
      </div>

      {/* Drag Handle */}
      <div {...attributes} {...listeners} aria-label={`Drag ${player?.first_name || ''} ${player?.last_name || ''}`} className={`roster-drag-handle ${registration.roster_locked ? "cursor-not-allowed text-slate-300" : "text-slate-400 hover:text-slate-600"}`}>
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Player Photo */}
      <RosterAvatar src={player?.photo_url} firstName={player?.first_name} lastName={player?.last_name} />

      {/* Player Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 break-words">
          {player?.first_name} {player?.last_name}
        </p>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {age && <span>{age}y</span>}
          {player?.gender && <span>• {player.gender}</span>}
          {registration.division_name && <span>• {registration.division_name}</span>}
        </div>
      </div>

      {/* Payment Status */}
      <div
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          isPaid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
        }`}
      >
        {isPaid ? "Paid" : "Pending"}
      </div>
      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); onToggleLock?.(registration); }}
        className={`rounded-md p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${registration.roster_locked ? "bg-amber-100 text-amber-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
        aria-label={registration.roster_locked ? `Unlock ${player?.first_name || "player"} roster placement` : `Lock ${player?.first_name || "player"} roster placement`}
        title={registration.roster_locked ? registration.roster_lock_reason || "Unlock roster placement" : "Lock roster placement"}
      >
        {registration.roster_locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
      </button>
      <button type="button" onClick={(event) => { event.stopPropagation(); onEditConstraints?.(registration); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" aria-label={`Edit roster constraints for ${player?.first_name || "player"}`} title="Roster constraints">
        <Settings className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// Drag Overlay Player Card (shown while dragging)
const DragOverlayPlayer = ({ registration, player }) => {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border border-indigo-400 bg-indigo-50 shadow-lg cursor-grabbing">
      <RosterAvatar src={player?.photo_url} firstName={player?.first_name} lastName={player?.last_name} />
      <p className="text-sm font-medium text-slate-900">
        {player?.first_name} {player?.last_name}
      </p>
    </div>
  );
};

// Droppable Team Column Component
const TeamColumn = ({
  team,
  registrations,
  onRemovePlayer,
  onEditTeam,
  onDeleteTeam,
  onPublishTeam,
  onUnpublishTeam,
  onManageStaff,
  publishingTeam,
  isGroupMode,
  squadlineStatus,
  onSyncTeam,
  syncingTeamId,
  onResendPlayerInvite,
  resendingPlayerInvite,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: team.team_id,
    data: { team },
  });

  const isPublished = team.status === "published";
  const isPublishing = publishingTeam === team.team_id;
  const isSyncing = syncingTeamId === team.team_id;
  const playerCount = registrations?.length || 0;
  const entityName = isGroupMode ? "camper" : "player";
  
  // Squadline sync status
  const isSyncedToSquadline = team.squadline_sync_status === "success";
  const hasSyncFailed = team.squadline_sync_status === "failed";
  const lastSyncedAt = team.squadline_synced_at;
  
  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      ref={setNodeRef}
      className={`roster-selected-team flex flex-col bg-white border rounded-2xl overflow-hidden min-w-[280px] max-w-[320px] transition-all duration-150 ${
        isOver 
          ? "border-indigo-400 ring-2 ring-indigo-200 shadow-lg scale-[1.02]" 
          : "border-slate-200 hover:border-slate-300"
      }`}
      data-testid={`team-column-${team.team_id}`}
    >
      {/* Team Header */}
      <div
        className="p-3 flex items-center justify-between program-team-heading"

      >
        <div className="flex items-center gap-2">

          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="font-semibold text-slate-900 text-sm">{team.name}</h4>
              <span
                className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                  isPublished ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                }`}
              >
                {isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
            <p className="roster-team-meta">{playerCount} {entityName}{playerCount !== 1 ? 's' : ''}{team.max_players ? ` / ${team.max_players} max` : ''}{Array.isArray(team.staff) ? ` · ${team.staff.length} staff` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isPublished ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnpublishTeam(team.team_id)}
              disabled={isPublishing}
              className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50"
              title="Unpublish team"
              aria-label={`Unpublish ${team.name}`}
            >
              {isPublishing ? (
                <div className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
              ) : (
                <EyeOff className="w-3.5 h-3.5" />
              )}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPublishTeam(team.team_id)}
              disabled={isPublishing}
              className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
              title="Publish team"
              aria-label={`Publish ${team.name}`}
            >
              {isPublishing ? (
                <div className="w-3 h-3 border-2 border-green-300 border-t-green-600 rounded-full animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
            </Button>
          )}

          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onEditTeam(team)} 
            className="roster-manage-team"
            title="Edit team"
            aria-label={`Edit ${team.name}`}
          >
            Manage team
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDeleteTeam(team.team_id)}
            title="Delete team"
            aria-label={`Delete ${team.name}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <section className="roster-staff-section" aria-label={`Coaches and staff for ${team.name}`}>
        <div className="roster-section-title"><h5>Coaches & staff</h5>
          <Button variant="ghost" size="sm" onClick={() => onManageStaff && onManageStaff(team)} className="program-manage-staff" title="Manage staff" aria-label={`Manage staff for ${team.name}`} data-testid={`manage-staff-btn-${team.team_id}`}>
            {Array.isArray(team.staff) && !team.staff.length && !team.coach_name ? 'Assign staff' : 'Manage staff'}
          </Button>
        </div>
        <div className="program-team-staff-summary">
          {Array.isArray(team.staff) && team.staff.length > 0 ? team.staff.map((member, index) => (
            <p className="roster-staff-row" key={`${member.staff_type || 'staff'}-${member.staff_id || index}`}>
              <span className="roster-photo-identity"><RosterAvatar src={member.avatar_url} firstName={member.first_name} lastName={member.last_name} /><strong>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Name unavailable'}</strong></span><span className="sr-only"> — </span><span>{{ head_coach: 'Head Coach', assistant_coach: 'Assistant Coach', team_manager: 'Team Manager', director: 'Director', coordinator: 'Coordinator', trainer: 'Trainer' }[member.role] || member.role || 'Role not specified'}</span>
            </p>
          )) : team.coach_name ? <p>Legacy coach: {team.coach_name}</p>
            : <p>{Array.isArray(team.staff) ? 'No staff assigned' : 'Staff details unavailable'}</p>}
        </div>
      </section>

      {/* Squadline Sync Status */}
      {squadlineStatus?.org_connected && (
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-white border border-slate-200 rounded flex items-center justify-center flex-shrink-0">
                <img 
                  src={SQUADLINE_LOGO} 
                  alt="SQ" 
                  className="h-3 w-3 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-black text-[8px] font-bold">S</span>';
                  }}
                />
              </div>
              {team.squadline_sync_status === "deleted_from_squadline" ? (
                <div className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 text-amber-500" />
                  <span className="text-xs text-amber-600 font-medium">Needs re-sync</span>
                </div>
              ) : isSyncedToSquadline ? (
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">Synced</span>
                  {lastSyncedAt && (
                    <span className="text-xs text-slate-400">• {formatRelativeTime(lastSyncedAt)}</span>
                  )}
                </div>
              ) : hasSyncFailed ? (
                <div className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 text-red-500" />
                  <span className="text-xs text-red-600 font-medium">Sync failed</span>
                </div>
              ) : (
                <span className="text-xs text-slate-400">Not synced</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSyncTeam && onSyncTeam(team)}
              disabled={isSyncing}
              className={`h-6 px-2 text-xs ${team.squadline_sync_status === "deleted_from_squadline" ? "text-amber-600 bg-amber-50 hover:bg-amber-100" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}`}
              title={team.squadline_sync_status === "deleted_from_squadline" ? "Re-sync to Squadline (required)" : "Sync to Squadline"}
            >
              {isSyncing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </Button>
          </div>
          {isSyncedToSquadline && team.squadline_roster_count !== undefined && (
            <p className="text-[10px] text-slate-400 mt-1 ml-7">
              {team.squadline_roster_count} {entityName}{team.squadline_roster_count !== 1 ? "s" : ""} synced
            </p>
          )}
        </div>
      )}

      <section className="roster-players-section" aria-label={`${entityName}s on ${team.name}`}>
        <div className="roster-section-title"><h5>{isGroupMode ? 'Campers' : 'Players'}</h5><span>{playerCount} assigned</span></div>
        <div className="roster-player-table-wrap">
          <table className="roster-player-table">
            <thead><tr><th scope="col">Player</th><th scope="col">Division</th><th scope="col">Payment</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {registrations?.length ? registrations.map(reg => (
                <tr key={reg.registration_id}>
                  <td className="program-assigned-identity"><div className="roster-photo-identity"><RosterAvatar src={reg.player?.photo_url} firstName={reg.player?.first_name} lastName={reg.player?.last_name} /><div><p>{reg.player?.first_name} {reg.player?.last_name}</p>{reg.roster_locked && <small>Roster locked</small>}</div></div></td>
                  <td>{reg.division_name || '—'}</td>
                  <td>{reg.payment_status || '—'}</td>
                  <td><div className="roster-row-actions">
                    {team.squadline_team_id && onResendPlayerInvite && (
                      <Button variant="ghost" size="sm" onClick={() => onResendPlayerInvite(team.team_id, reg.registration_id, `${reg.player?.first_name} ${reg.player?.last_name}`)} disabled={resendingPlayerInvite === reg.registration_id} title="Resend Squadline invite to parent" aria-label={`Resend Squadline invite for ${reg.player?.first_name || 'player'}`} data-testid={`resend-player-invite-${reg.registration_id}`}>
                        {resendingPlayerInvite === reg.registration_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="roster-remove-player" onClick={() => onRemovePlayer(team.team_id, reg.registration_id)} aria-label={`Remove ${reg.player?.first_name || "player"} from ${team.name}`}>Remove</Button>
                  </div></td>
                </tr>
              )) : <tr><td colSpan={4} className="roster-empty-players">No {entityName}s assigned yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <div className={`roster-welcome-drop ${isOver ? 'roster-welcome-drop-active' : ''}`}>
        <UserPlus aria-hidden="true" className="h-5 w-5" />
        <strong>Drop {entityName}s or staff here</strong>
        <p>Selected team: {team.name}</p>
      </div>
    </div>
  );
};

// Unassigned Players Drop Zone
const UnassignedDropZone = ({ isOver }) => {
  const { setNodeRef } = useDroppable({
    id: "unassigned",
    data: { isUnassigned: true },
  });

  return (
    <div
      ref={setNodeRef}
      className={`mt-4 p-4 border-2 border-dashed rounded-xl transition-all ${
        isOver ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50"
      }`}
    >
      <p className="text-sm text-slate-500 text-center">
        Drop here to unassign from team
      </p>
    </div>
  );
};

// Main TeamBuilder Component
export const TeamBuilder = ({
  teams,
  registrations,
  programId,
  token,
  API,
  onRefresh,
  onCreateTeam,
  onEditTeam,
  onDeleteTeam,
  onPublishTeam,
  onUnpublishTeam,
  onPublishAllTeams,
  publishingTeam,
  publishingAll = false,
  isGroupMode = false,
  fullPage = false,
  // Squadline props
  squadlineStatus = null,
  squadlineLoading = false,
  onConnectSquadline = null,
  onSyncToSquadline = null,
  onToggleSquadline = null,
  enablingSquadline = false,
}) => {
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [divisionFilter, setDivisionFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("board");

  // Selection state
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeType, setActiveType] = useState(null); // 'player' or 'staff'

  // Typed eligibility belongs to the currently selected team, never to an org-wide coach pool.
  const [selectedTeamId, setSelectedTeamId] = useState(teams[0]?.team_id || "");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const selectedTeam = teams.find(team => team.team_id === selectedTeamId) || teams[0] || null;
  const currentTeamId = selectedTeam?.team_id || "";
  const [availableTab, setAvailableTab] = useState("players");
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedStaffRole, setSelectedStaffRole] = useState("assistant_coach");
  const [addingStaff, setAddingStaff] = useState(false);
  const staffEpoch = useRef(0);
  const staffMutation = useRef(false);
  const activeTeamRef = useRef(currentTeamId);
  activeTeamRef.current = currentTeamId;

  // Auto-balance modal state
  const [showAutoBalanceModal, setShowAutoBalanceModal] = useState(false);
  const [balanceStrategy, setBalanceStrategy] = useState("count");
  const [redistributeAll, setRedistributeAll] = useState(false);
  const [isBalancing, setIsBalancing] = useState(false);
  const [balancePreview, setBalancePreview] = useState(null);
  const [lastRosterBatch, setLastRosterBatch] = useState(null);
  const [undoingBatch, setUndoingBatch] = useState(false);
  const [lockingRegistrationId, setLockingRegistrationId] = useState(null);
  const [releasePreview, setReleasePreview] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [loadingReleasePreview, setLoadingReleasePreview] = useState(false);
  const [constraintsRegistration, setConstraintsRegistration] = useState(null);
  const [savingConstraints, setSavingConstraints] = useState(false);
  const [constraintsForm, setConstraintsForm] = useState({
    locked: false, lock_reason: "", keep_with_registration_id: "none", avoid_registration_id: "none",
    preferred_team_id: "none", preferred_positions: "", notes: "",
  });

  // Squadline sync state
  const [isSyncingSquadline, setIsSyncingSquadline] = useState(false);
  const [syncingTeamId, setSyncingTeamId] = useState(null);
  const [showPurchaseSeatsDialog, setShowPurchaseSeatsDialog] = useState(false);
  const [purchaseSeatsInfo, setPurchaseSeatsInfo] = useState(null);
  const [resendingPlayerInvite, setResendingPlayerInvite] = useState(null);

  // Team Staff Manager state - keeping for backwards compatibility
  const [showStaffManager, setShowStaffManager] = useState(false);
  const [selectedTeamForStaff, setSelectedTeamForStaff] = useState(null);

  const fetchAvailableStaff = useCallback(async () => {
    const epoch = ++staffEpoch.current;
    setAvailableStaff([]);
    setSelectedStaffId("");
    setStaffError("");
    if (!currentTeamId) { setLoadingStaff(false); return; }
    setLoadingStaff(true);
    try {
      const response = await axios.get(`${API}/teams/${currentTeamId}/available-staff`, { headers: { Authorization: `Bearer ${token}` } });
      if (epoch !== staffEpoch.current || currentTeamId !== activeTeamRef.current) return;
      setAvailableStaff(response.data.staff || []);
    } catch (error) {
      if (epoch !== staffEpoch.current || currentTeamId !== activeTeamRef.current) return;
      setStaffError("Could not load eligible staff. Retry to continue.");
    } finally {
      if (epoch === staffEpoch.current && currentTeamId === activeTeamRef.current) setLoadingStaff(false);
    }
  }, [API, token, currentTeamId]);

  useEffect(() => {
    setAddingStaff(false);
    staffMutation.current = false;
    setSelectedPlayers([]);
    setActiveId(null);
    setActiveType(null);
    setSelectedStaffRole("assistant_coach");
    fetchAvailableStaff();
    return () => { staffEpoch.current += 1; };
  }, [fetchAvailableStaff]);

  const handleManageTeamStaff = (team) => {
    setSelectedTeamForStaff(team);
    setShowStaffManager(true);
  };

  const filteredStaff = availableStaff.filter(person =>
    !(selectedTeam?.staff || []).some(member => member.staff_id === person.staff_id) &&
    `${person.first_name || ""} ${person.last_name || ""} ${person.email || ""}`.toLowerCase().includes(staffSearchQuery.toLowerCase())
  );

  // Click and drag share the canonical typed mutation. Never split or coerce an ID.
  const handleAddStaffToTeam = async (key, teamId) => {
    if (teamId !== currentTeamId || teamId !== activeTeamRef.current || loadingStaff || staffMutation.current) return;
    const person = filteredStaff.find(candidate => staffKey(candidate) === key);
    if (!person) return;
    const epoch = staffEpoch.current;
    const mutation = {};
    staffMutation.current = mutation;
    setAddingStaff(true);
    setStaffError("");
    try {
      await axios.post(`${API}/teams/${teamId}/staff`, {
        staff_id: person.staff_id, staff_type: person.staff_type, role: selectedStaffRole,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (epoch !== staffEpoch.current || teamId !== activeTeamRef.current) return;
      setSelectedStaffId("");
      toast.success("Staff member added. Changes saved immediately.");
      onRefresh?.();
      fetchAvailableStaff();
    } catch (error) {
      if (epoch !== staffEpoch.current || teamId !== activeTeamRef.current) return;
      const detail = error.response?.data?.detail;
      setStaffError(typeof detail === "string" ? detail : "Could not add staff. Refresh eligible staff and try again.");
    } finally {
      if (staffMutation.current === mutation && teamId === activeTeamRef.current) { staffMutation.current = false; setAddingStaff(false); }
    }
  };

  // DnD sensors - optimized for faster response
  const sensors = useSensors(
    useSensor(PointerSensor, { 
      activationConstraint: { 
        distance: 3,  // Reduced from 5 for faster drag start
      } 
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,  // Short delay for touch
        tolerance: 5,
      }
    }),
    useSensor(KeyboardSensor)
  );
  
  // Optimistic updates state - for instant UI feedback
  const [pendingAssignments, setPendingAssignments] = useState({});

  // Entity labels based on mode
  const entityName = isGroupMode ? "Group" : "Team";
  const playerName = isGroupMode ? "Camper" : "Player";

  // Get unique divisions for filter
  const divisions = useMemo(() => {
    const divisionMap = new Map();
    registrations.forEach((reg) => {
      const value = reg.division_id || reg.division_name;
      if (value) divisionMap.set(value, reg.division_name || reg.division_id);
    });
    return Array.from(divisionMap, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [registrations]);

  // Filter registrations for sidebar
  const filteredRegistrations = useMemo(() => {
    return registrations.filter((reg) => {
      const player = reg.player;
      if (!player) return false;

      // Search filter
      if (searchQuery) {
        const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
        if (!fullName.includes(searchQuery.toLowerCase())) return false;
      }

      // Payment filter
      if (paymentFilter !== "all") {
        if (paymentFilter === "paid" && reg.payment_status !== "paid") return false;
        if (paymentFilter === "pending" && reg.payment_status === "paid") return false;
      }

      // Assignment filter
      if (assignmentFilter !== "all") {
        const isAssigned = !!reg.team_id;
        if (assignmentFilter === "assigned" && !isAssigned) return false;
        if (assignmentFilter === "unassigned" && isAssigned) return false;
      }

      // Gender filter
      if (genderFilter !== "all" && player.gender !== genderFilter) return false;

      // Division filter
      if (divisionFilter !== "all" && (reg.division_id || reg.division_name) !== divisionFilter) return false;

      return true;
    });
  }, [registrations, searchQuery, paymentFilter, assignmentFilter, genderFilter, divisionFilter]);

  const visibleTeams = useMemo(() => (
    divisionFilter === "all" ? teams : teams.filter((team) => team.division_id === divisionFilter)
  ), [divisionFilter, teams]);

  // Get registrations per team - with optimistic updates
  const teamRegistrations = useMemo(() => {
    const map = {};
    teams.forEach((team) => {
      map[team.team_id] = registrations.filter((reg) => {
        // Check for optimistic/pending assignment
        const pendingTeamId = pendingAssignments[reg.registration_id];
        if (pendingTeamId !== undefined) {
          return pendingTeamId === team.team_id;
        }
        return reg.team_id === team.team_id;
      });
    });
    return map;
  }, [teams, registrations, pendingAssignments]);

  // Unassigned registrations - with optimistic updates
  const unassignedRegistrations = useMemo(() => {
    return registrations.filter((reg) => {
      // Check for optimistic/pending assignment
      const pendingTeamId = pendingAssignments[reg.registration_id];
      if (pendingTeamId !== undefined) {
        return pendingTeamId === null;
      }
      return !reg.team_id;
    });
  }, [registrations, pendingAssignments]);

  // Toggle player selection
  const togglePlayerSelection = (registrationId) => {
    setSelectedPlayers((prev) =>
      prev.includes(registrationId)
        ? prev.filter((id) => id !== registrationId)
        : [...prev, registrationId]
    );
  };

  // Select all filtered players
  const selectAllFiltered = () => {
    const allIds = filteredRegistrations.map((r) => r.registration_id);
    setSelectedPlayers(allIds);
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedPlayers([]);
  };

  const applyRosterMoves = async (moves, reason) => {
    const response = await axios.post(
      `${API}/programs/${programId}/roster/batches`,
      { idempotency_key: createIdempotencyKey(), moves, reason },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.data?.batch) setLastRosterBatch(response.data.batch);
    return response.data?.batch;
  };

  const handleToggleRosterLock = async (registration) => {
    const locked = !registration.roster_locked;
    setLockingRegistrationId(registration.registration_id);
    try {
      await axios.put(
        `${API}/programs/${programId}/roster/locks/${registration.registration_id}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { locked, reason: locked ? "Protected by administrator" : undefined },
        },
      );
      toast.success(locked ? "Roster placement locked" : "Roster placement unlocked");
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update roster lock");
    } finally {
      setLockingRegistrationId(null);
    }
  };

  const handleUndoLastBatch = async () => {
    if (!lastRosterBatch?.batch_id) return;
    setUndoingBatch(true);
    try {
      await axios.post(
        `${API}/programs/${programId}/roster/batches/${lastRosterBatch.batch_id}/undo`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { idempotency_key: createIdempotencyKey("undo") },
        },
      );
      toast.success("Last roster change restored");
      setLastRosterBatch(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Could not undo roster change");
    } finally {
      setUndoingBatch(false);
    }
  };

  const handleRequestRelease = async (team = null) => {
    setReleaseTarget(team || { all: true });
    setReleasePreview(null);
    setLoadingReleasePreview(true);
    try {
      const url = team
        ? `${API}/teams/${team.team_id}/publish-preview`
        : `${API}/programs/${programId}/publish-teams-preview`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setReleasePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load the roster release review");
      setReleaseTarget(null);
    } finally {
      setLoadingReleasePreview(false);
    }
  };

  const handleConfirmRelease = async () => {
    if (!releasePreview?.ready) return;
    if (releaseTarget?.all) await onPublishAllTeams?.();
    else if (releaseTarget?.team_id) await onPublishTeam?.(releaseTarget.team_id);
    setReleaseTarget(null);
    setReleasePreview(null);
  };

  const openRosterConstraints = (registration) => {
    const constraints = registration.roster_constraints || {};
    setConstraintsRegistration(registration);
    setConstraintsForm({
      locked: Boolean(registration.roster_locked),
      lock_reason: registration.roster_lock_reason || "",
      keep_with_registration_id: constraints.keep_with_registration_ids?.[0] || "none",
      avoid_registration_id: constraints.avoid_registration_ids?.[0] || "none",
      preferred_team_id: constraints.preferred_team_id || "none",
      preferred_positions: (constraints.preferred_positions || []).join(", "),
      notes: constraints.notes || "",
    });
  };

  const handleSaveConstraints = async () => {
    if (!constraintsRegistration) return;
    setSavingConstraints(true);
    try {
      const splitValues = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);
      await axios.put(
        `${API}/programs/${programId}/roster/constraints/${constraintsRegistration.registration_id}`,
        {
          locked: constraintsForm.locked,
          lock_reason: constraintsForm.locked ? constraintsForm.lock_reason || null : null,
          keep_with_registration_ids: constraintsForm.keep_with_registration_id === "none" ? [] : [constraintsForm.keep_with_registration_id],
          avoid_registration_ids: constraintsForm.avoid_registration_id === "none" ? [] : [constraintsForm.avoid_registration_id],
          preferred_team_id: constraintsForm.preferred_team_id === "none" ? null : constraintsForm.preferred_team_id,
          preferred_positions: splitValues(constraintsForm.preferred_positions),
          notes: constraintsForm.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Roster constraints saved");
      setConstraintsRegistration(null);
      onRefresh?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Could not save roster constraints");
    } finally {
      setSavingConstraints(false);
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    // Check if this is a staff drag
    if (event.active.data?.current?.type === "staff") {
      setActiveType("staff");
    } else {
      setActiveType("player");
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    const currentType = activeType;
    setActiveType(null);

    if (!over) return;

    // Handle staff drag-drop
    if (currentType === "staff" && active.data?.current?.type === "staff") {
      const person = active.data.current.person;
      if (active.data.current.teamId !== currentTeamId) return;
      const targetTeamId = over.id;
      
      // Don't allow dropping on "unassigned"
      if (targetTeamId === "unassigned") return;
      
      // Add staff to team
      await handleAddStaffToTeam(staffKey(person), targetTeamId);
      return;
    }

    // Handle player drag-drop (existing logic)
    const registrationId = active.id;
    const targetTeamId = over.id;

    // Find the registration
    const registration = registrations.find((r) => r.registration_id === registrationId);
    if (!registration) return;

    // If dropped on same team, do nothing
    if (registration.team_id === targetTeamId) return;

    // If dropped on "unassigned" zone
    if (targetTeamId === "unassigned") {
      if (registration.team_id) {
        // Optimistic update - immediately show as unassigned
        setPendingAssignments(prev => ({ ...prev, [registrationId]: null }));
        
        try {
          await applyRosterMoves([{ registration_id: registrationId, to_team_id: null }], "Manual roster unassignment");
          toast.success(`${playerName} removed from ${entityName.toLowerCase()}`);
          onRefresh?.();
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : detail?.message || `Failed to remove ${playerName.toLowerCase()}`);
          onRefresh?.();
        } finally {
          setPendingAssignments(prev => {
            const { [registrationId]: _, ...rest } = prev;
            return rest;
          });
        }
      }
      return;
    }

    // Optimistic update - immediately show player in new team
    setPendingAssignments(prev => ({ ...prev, [registrationId]: targetTeamId }));

    // The server moves an existing assignment in one write, so a failed move cannot strand the player.
    try {
      await applyRosterMoves([{ registration_id: registrationId, to_team_id: targetTeamId }], "Manual drag-and-drop roster move");

      toast.success(`${playerName} assigned to ${entityName.toLowerCase()}`);
      // Keep optimistic placement until authoritative readback; otherwise an
      // immediate Undo/touch drag can race an older assigned-roster response.
      await onRefresh();
    } catch (error) {
      console.error("Error assigning player:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || `Failed to assign ${playerName.toLowerCase()}`);
      // Revert optimistic update on error
      onRefresh();
    } finally {
      // Clear pending state
      setPendingAssignments(prev => {
        const { [registrationId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle remove from team
  const handleRemoveFromTeam = async (teamId, registrationId) => {
    try {
      await applyRosterMoves([{ registration_id: registrationId, to_team_id: null }], "Manual roster unassignment");
      toast.success(`${playerName} removed from ${entityName.toLowerCase()}`);
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || `Failed to remove ${playerName.toLowerCase()}`);
    }
  };

  // Bulk assign selected players to a team
  const handleBulkAssign = async (teamId) => {
    if (selectedPlayers.length === 0) {
      toast.error(`No ${playerName.toLowerCase()}s selected`);
      return;
    }

    try {
      await applyRosterMoves(
        selectedPlayers.map((registrationId) => ({ registration_id: registrationId, to_team_id: teamId })),
        "Bulk roster assignment",
      );

      toast.success(`${selectedPlayers.length} ${playerName.toLowerCase()}(s) assigned`);
      setSelectedPlayers([]);
      onRefresh();
    } catch (error) {
      console.error("Error bulk assigning:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to assign players");
    }
  };

  // Auto-balance teams - open modal
  const handleAutoBalanceClick = () => {
    if (visibleTeams.length < 2) {
      toast.error(`Need at least 2 ${entityName.toLowerCase()}s to auto-balance`);
      return;
    }
    setBalancePreview(null);
    setShowAutoBalanceModal(true);
  };

  const handlePreviewBalance = async () => {
    setIsBalancing(true);
    try {
      const response = await axios.post(
        `${API}/programs/${programId}/roster/balance/preview`,
        {
          strategy: balanceStrategy,
          redistribute_all: redistributeAll,
          division_id: divisionFilter === "all" ? null : divisionFilter,
          locked_registration_ids: registrations.filter((registration) => registration.roster_locked).map((registration) => registration.registration_id),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBalancePreview(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to preview balance");
    } finally {
      setIsBalancing(false);
    }
  };

  const handleApplyBalance = async () => {
    if (!balancePreview?.preview_token) return;
    setIsBalancing(true);
    try {
      const response = await axios.post(
        `${API}/programs/${programId}/roster/balance/apply`,
        { idempotency_key: createIdempotencyKey("balance"), preview_token: balancePreview.preview_token },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.batch) setLastRosterBatch(response.data.batch);
      toast.success(`${balancePreview.moves.length} roster move${balancePreview.moves.length === 1 ? "" : "s"} applied`);
      setShowAutoBalanceModal(false);
      setBalancePreview(null);
      onRefresh?.();
    } catch (error) {
      console.error("Error auto-balancing:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to apply balance");
    } finally {
      setIsBalancing(false);
    }
  };

  // Handle Squadline sync
  const handleSyncToSquadline = async () => {
    if (isSyncingSquadline) return;
    
    if (teams.length === 0) {
      toast.error(`No ${entityName.toLowerCase()}s to sync`);
      return;
    }

    try {
      setIsSyncingSquadline(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Sync each team to Squadline
      let syncedCount = 0;
      let failedTeams = [];
      
      for (const team of teams) {
        try {
          const teamRegs = registrations.filter(r => r.team_id === team.team_id);
          const roster = teamRegs.map(r => ({
            // Player identification
            player_id: r.player?.player_id,
            registration_id: r.registration_id,
            
            // Player basic info
            first_name: r.player?.first_name,
            last_name: r.player?.last_name,
            email: r.player?.email || r.parent_email,
            phone: r.player?.phone || r.household?.primary_contact?.phone,
            date_of_birth: r.player?.date_of_birth,
            gender: r.player?.gender,
            
            // Address
            address: {
              street: r.player?.address?.street || r.player?.address?.line1 || r.household?.address?.street,
              city: r.player?.address?.city || r.household?.address?.city,
              state: r.player?.address?.state || r.household?.address?.state,
              zip: r.player?.address?.zip || r.player?.address?.postal_code || r.household?.address?.zip,
            },
            
            // Parent/Guardian info
            parent_name: r.household?.primary_contact?.name || r.payer?.name,
            parent_email: r.household?.primary_contact?.email || r.payer?.email,
            parent_phone: r.household?.primary_contact?.phone,
            
            // Emergency contact
            emergency_contact_name: r.player?.emergency_contact_name,
            emergency_contact_phone: r.player?.emergency_contact_phone,
            emergency_contact_relationship: r.player?.emergency_contact_relationship,
            
            // Medical info
            medical_conditions: r.player?.medical_conditions,
            allergies: r.player?.allergies,
            medications: r.player?.medications,
            
            // Sizes
            jersey_size: r.jersey_size || r.player?.jersey_size,
            short_size: r.short_size || r.player?.short_size,
            
            // Team assignment
            position: r.position,
            jersey_number: r.jersey_number,
          }));

          await axios.post(
            `${API}/squadline/teams/sync/${team.organization_id || squadlineStatus?.org_id}`,
            {
              team_id: team.team_id,
              name: team.name,
              sport: team.sport,
              age_group: team.age_group,
              roster: roster
            },
            { headers }
          );
          syncedCount++;
        } catch (teamError) {
          console.error(`Failed to sync team ${team.name}:`, teamError);
          const errorMsg = teamError.response?.data?.detail || teamError.message || 'Unknown error';
          failedTeams.push({ name: team.name, error: errorMsg });
        }
      }
      
      // Show results
      if (syncedCount > 0) {
        toast.success(
          <div className="flex items-center gap-2">
            <img src={SQUADLINE_LOGO} alt="Squadline" className="h-4" />
            <span>{syncedCount} {entityName.toLowerCase()}(s) synced to Squadline</span>
          </div>
        );
      }
      
      if (failedTeams.length > 0) {
        toast.error(
          <div>
            <p className="font-medium">Failed to sync {failedTeams.length} team(s):</p>
            {failedTeams.slice(0, 3).map((t, i) => (
              <p key={i} className="text-sm">{t.name}: {t.error}</p>
            ))}
            {failedTeams.length > 3 && <p className="text-sm">...and {failedTeams.length - 3} more</p>}
          </div>,
          { duration: 10000 }
        );
      }
      
      if (onSyncToSquadline) {
        onSyncToSquadline();
      }
      onRefresh();
    } catch (error) {
      console.error("Error syncing to Squadline:", error);
      toast.error(error.response?.data?.detail || "Failed to sync to Squadline");
    } finally {
      setIsSyncingSquadline(false);
    }
  };

  // Handle individual team sync to Squadline
  const handleSyncSingleTeam = async (team) => {
    if (syncingTeamId) return;
    
    const teamRegs = registrations.filter(r => r.team_id === team.team_id);
    // Allow syncing even with no players - staff/coaches can still receive invites

    try {
      setSyncingTeamId(team.team_id);
      const headers = { Authorization: `Bearer ${token}` };
      
      const roster = teamRegs.map(r => ({
        // Player identification
        player_id: r.player?.player_id,
        registration_id: r.registration_id,
        
        // Player basic info
        first_name: r.player?.first_name,
        last_name: r.player?.last_name,
        email: r.player?.email || r.parent_email,
        phone: r.player?.phone || r.household?.primary_contact?.phone,
        date_of_birth: r.player?.date_of_birth,
        gender: r.player?.gender,
        
        // Address
        address: {
          street: r.player?.address?.street || r.player?.address?.line1 || r.household?.address?.street,
          city: r.player?.address?.city || r.household?.address?.city,
          state: r.player?.address?.state || r.household?.address?.state,
          zip: r.player?.address?.zip || r.player?.address?.postal_code || r.household?.address?.zip,
        },
        
        // Parent/Guardian info
        parent_name: r.household?.primary_contact?.name || r.payer?.name,
        parent_email: r.household?.primary_contact?.email || r.payer?.email,
        parent_phone: r.household?.primary_contact?.phone,
        
        // Emergency contact
        emergency_contact_name: r.player?.emergency_contact_name,
        emergency_contact_phone: r.player?.emergency_contact_phone,
        emergency_contact_relationship: r.player?.emergency_contact_relationship,
        
        // Medical info
        medical_conditions: r.player?.medical_conditions,
        allergies: r.player?.allergies,
        medications: r.player?.medications,
        
        // Sizes
        jersey_size: r.jersey_size || r.player?.jersey_size,
        short_size: r.short_size || r.player?.short_size,
        
        // Team assignment
        position: r.position,
        jersey_number: r.jersey_number,
      }));

      await axios.post(
        `${API}/squadline/teams/sync/${team.organization_id || squadlineStatus?.org_id}`,
        {
          team_id: team.team_id,
          name: team.name,
          sport: team.sport,
          age_group: team.age_group,
          roster: roster
        },
        { headers }
      );
      
      toast.success(
        <div className="flex items-center gap-2">
          <img src={SQUADLINE_LOGO} alt="Squadline" className="h-4 w-4" />
          <span>{team.name} synced to Squadline</span>
        </div>
      );
      
      onRefresh();
    } catch (error) {
      console.error(`Failed to sync team ${team.name}:`, error);
      
      // Check if it's a "no seats available" error
      const errorDetail = error.response?.data?.detail;
      if (error.response?.status === 402 || 
          (typeof errorDetail === 'object' && errorDetail?.error === 'no_seats_available')) {
        // Show purchase seats dialog
        setPurchaseSeatsInfo({
          teamName: team.name,
          seatsUsed: errorDetail?.seats_used || 0,
          seatsPurchased: errorDetail?.seats_purchased || 0,
          seatsAvailable: errorDetail?.seats_available || 0
        });
        setShowPurchaseSeatsDialog(true);
      } else if (typeof errorDetail === 'string' && 
                 (errorDetail.includes('Not enough seats') || 
                  errorDetail.includes('No Squadline seats available') ||
                  errorDetail.includes('purchase more seats'))) {
        // Also catch the string error format
        setPurchaseSeatsInfo({
          teamName: team.name,
          message: errorDetail
        });
        setShowPurchaseSeatsDialog(true);
      } else {
        toast.error(errorDetail?.message || errorDetail || `Failed to sync ${team.name}`);
      }
    } finally {
      setSyncingTeamId(null);
    }
  };

  // Handle resending Squadline invite to a player's parent/guardian
  const handleResendPlayerInvite = async (teamId, registrationId, playerName) => {
    const team = teams.find(t => t.team_id === teamId);
    if (!team?.squadline_team_id) {
      toast.error("Team is not synced to Squadline. Please sync the team first.");
      return;
    }

    setResendingPlayerInvite(registrationId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/squadline/resend-invite/${teamId}/player/${registrationId}`,
        {},
        { headers }
      );
      toast.success(
        <div className="flex items-center gap-2">
          <img src={SQUADLINE_LOGO} alt="Squadline" className="h-4" />
          <span>Invite sent to {response.data.email || playerName + "'s parent"}</span>
        </div>
      );
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to resend invite";
      toast.error(message);
    } finally {
      setResendingPlayerInvite(null);
    }
  };

  // Get active drag item
  const activeDragItem = activeId
    ? registrations.find((r) => r.registration_id === activeId)
    : null;

  const rosterReadiness = useMemo(() => {
    const scopedRegistrations = divisionFilter === "all"
      ? registrations
      : registrations.filter((registration) => (registration.division_id || registration.division_name) === divisionFilter);
    const unassigned = scopedRegistrations.filter((registration) => !registration.team_id && ["confirmed", "pre_registered"].includes(registration.status)).length;
    const paymentIssues = scopedRegistrations.filter((registration) => registration.team_id && registration.payment_status !== "paid").length;
    const overCapacity = visibleTeams.filter((team) => {
      const capacity = team.max_players;
      return capacity && (teamRegistrations[team.team_id]?.length || 0) > capacity;
    }).length;
    const missingCoach = visibleTeams.filter((team) => !team.coach_id && !(team.staff || []).some((staff) => staff.role === "head_coach")).length;
    const syncIssues = visibleTeams.filter((team) => squadlineStatus?.org_connected && team.squadline_sync_status === "failed").length;
    return { unassigned, paymentIssues, overCapacity, missingCoach, syncIssues };
  }, [divisionFilter, registrations, squadlineStatus?.org_connected, teamRegistrations, visibleTeams]);

  return (
    <div className="program-section program-teams" data-program-section="teams">
      <div className="program-section-heading"><div><h2>Team Builder</h2><p>Choose a {isGroupMode ? 'group' : 'team'}, then select or drag players and staff into its roster.</p></div></div>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
          {/* Header Actions */}
          <div className="roster-toolbar">
            <div className="flex flex-wrap items-center gap-2">
              {lastRosterBatch?.batch_id && (
                <Button variant="outline" size="sm" onClick={handleUndoLastBatch} disabled={undoingBatch || Object.keys(pendingAssignments).length > 0} className="rounded-full" data-testid="undo-roster-batch">
                  {undoingBatch ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
                  Undo
                </Button>
              )}
              <div className="flex rounded-full border border-slate-200 bg-white p-0.5" aria-label="Roster view">
                <button type="button" onClick={() => setViewMode("board")} className={`rounded-full p-1.5 ${viewMode === "board" ? "bg-slate-900 text-white" : "text-slate-500"}`} aria-label="Board view" aria-pressed={viewMode === "board"}><LayoutGrid className="h-4 w-4" /></button>
                <button type="button" onClick={() => setViewMode("table")} className={`rounded-full p-1.5 ${viewMode === "table" ? "bg-slate-900 text-white" : "text-slate-500"}`} aria-label="Table view" aria-pressed={viewMode === "table"}><List className="h-4 w-4" /></button>
              </div>
              {visibleTeams.length >= 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAutoBalanceClick}
                  className="rounded-full"
                  data-testid="auto-balance-btn"
                >
                  <Scale className="w-4 h-4 mr-2" />
                  Auto-Balance
                </Button>
              )}
              {teams.some((team) => team.status !== "published") && onPublishAllTeams && (
                <Button variant="outline" size="sm" onClick={() => handleRequestRelease()} disabled={publishingAll} className="rounded-full" data-testid="review-release-all">
                  <Send className="mr-2 h-4 w-4" /> Review release
                </Button>
              )}

            </div>
          </div>

<details className="roster-program-tools"><summary>Roster readiness & Squadline</summary>          <div className="program-roster-readiness" aria-label="Roster readiness" data-testid="roster-readiness">
            {[
              ["Unassigned", rosterReadiness.unassigned, "amber"],
              ["Payment review", rosterReadiness.paymentIssues, "amber"],
              ["Over capacity", rosterReadiness.overCapacity, "red"],
              ["Missing coach", rosterReadiness.missingCoach, "amber"],
              ["Sync failures", rosterReadiness.syncIssues, "red"],
            ].map(([label, count, tone]) => (
              <div key={label} className={`rounded-xl border px-3 py-2 ${count ? (tone === "red" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50") : "border-emerald-200 bg-emerald-50"}`}>
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
                <p className="text-lg font-semibold text-slate-900">{count}</p>
              </div>
            ))}
          </div>

          {!squadlineStatus && (
            <div className="program-squadline-status" role="status">
              <div><strong>Squadline</strong><p>{squadlineLoading ? "Loading connection status…" : "Connection status is unavailable. Retry to check your organization's setup."}</p></div>
              {!squadlineLoading && onSyncToSquadline && <Button variant="outline" size="sm" onClick={onSyncToSquadline}>Retry Squadline status</Button>}
            </div>
          )}
          {/* Squadline Integration Banner - Compact */}
          {squadlineStatus?.org_connected && (
            <div
              className="program-squadline-status"
              data-testid="squadline-team-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src={SQUADLINE_LOGO}
                    alt="Squadline"
                    className="h-5 w-5 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <span className="text-xs text-slate-600">
                    <strong>Squadline</strong> · <span className="font-medium text-green-700">{squadlineStatus.enabled_teams || 0}</span>/{teams.length} synced
                  </span>
                  {squadlineStatus?.squadline_enabled && (
                    <span className="text-xs text-slate-500">
                      • <span className="font-medium text-blue-700">{Math.max(0, (squadlineStatus.org_seats_purchased || 0) - (squadlineStatus.org_seats_used || 0))}</span> licenses
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs ${squadlineStatus?.squadline_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                      {squadlineStatus?.squadline_enabled ? 'On' : 'Off'}
                    </span>
                    <Switch
                      checked={squadlineStatus?.squadline_enabled || false}
                      onCheckedChange={onToggleSquadline}
                      disabled={enablingSquadline}
                      className="scale-75"
                      aria-label="Enable Squadline for this program"
                      data-testid="squadline-toggle"
                    />
                  </div>

                  {squadlineStatus?.squadline_enabled && teams.length > 0 && (
                    <Button
                      size="sm"
                      onClick={handleSyncToSquadline}
                      disabled={isSyncingSquadline}
                      variant="ghost"
                      className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-100"
                      data-testid="sync-all-squadline-btn"
                    >
                      {isSyncingSquadline ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-3 h-3 mr-1" />
                          Sync All
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Connect to Squadline Banner - only show when not connected */}
          {squadlineStatus && !squadlineStatus.org_connected && (
            <div
              className="program-squadline-status"
              data-testid="squadline-connect-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    <img
                      src={SQUADLINE_LOGO}
                      alt="Squadline"
                      className="h-6 w-6 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span class="text-black text-lg font-bold">S</span>';
                      }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Connect to Squadline
                    </p>
                    <p className="text-xs text-slate-500">
                      Enable team communication app for parents
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onConnectSquadline}
                  className="rounded-full border-slate-300"
                  data-testid="connect-squadline-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Squadline
                </Button>
              </div>
            </div>
          )}

          {/* Unassigned Count Banner */}
          {unassignedRegistrations.length > 0 && (
            <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                {unassignedRegistrations.length} unassigned {playerName.toLowerCase()}
                {unassignedRegistrations.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

</details><p className="roster-save-note">Player moves save immediately. Undo restores the last batch; publishing and Squadline sync are separate.</p>
      <div className={`program-builder-layout roster-workspace ${fullPage ? "roster-workspace-full" : ""}`} data-testid="team-builder">
        <nav className="roster-team-list" aria-label="Teams">
          <div className="roster-panel-title"><h3>{entityName}s <span>{teams.length}</span></h3><Button variant="outline" size="sm" onClick={onCreateTeam} aria-label={`Create ${entityName}`} data-testid="create-team-btn"><Plus aria-hidden="true" className="h-3 w-3" /> New</Button></div>
          <Input aria-label={`Search ${entityName.toLowerCase()}s`} placeholder={`Search ${entityName.toLowerCase()}s`} value={teamSearchQuery} onChange={event => setTeamSearchQuery(event.target.value)} />
          <div className="roster-team-options">
          {teams.length ? teams.filter(team => team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())).map(team => <button key={team.team_id} type="button" aria-label={`Select team ${team.name}`} aria-pressed={team.team_id === currentTeamId} onClick={() => setSelectedTeamId(team.team_id)}>
            <strong>{team.name}</strong><span>{teamRegistrations[team.team_id]?.length || 0} players{Array.isArray(team.staff) ? ` · ${team.staff.length} staff` : ''}</span><small>{team.status === "published" ? "Published" : "Draft"}</small>
          </button>) : <p>No teams yet. Create your first team to build a roster.</p>}
          {teams.length > 0 && !teams.some(team => team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())) && <p>No teams match your search.</p>}
          </div>
          {selectedTeam && <p className="roster-selected-label">Selected team: {selectedTeam.name}</p>}
        </nav>
        {/* Left Sidebar - Player List */}
        <section className="program-builder-participants roster-available flex w-full flex-shrink-0 flex-col overflow-hidden border bg-white" aria-label="Available people">
          <h3 className="roster-available-title">Available</h3>
          <div className="roster-available-tabs" role="tablist" aria-label="Available people" onKeyDown={event => {
            if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === "Home" ? "players" : event.key === "End" ? "staff" : availableTab === "players" ? "staff" : "players";
            setAvailableTab(next);
            event.currentTarget.querySelector(`#available-${next}-tab`)?.focus();
          }}>
            <button type="button" role="tab" id="available-players-tab" aria-selected={availableTab === "players"} tabIndex={availableTab === "players" ? 0 : -1} aria-controls="available-players" onClick={() => setAvailableTab("players")}>Players</button>
            <button type="button" role="tab" id="available-staff-tab" aria-selected={availableTab === "staff"} tabIndex={availableTab === "staff" ? 0 : -1} aria-controls="available-staff" onClick={() => setAvailableTab("staff")}>Staff</button>
          </div>
          {availableTab === "players" ? <div role="tabpanel" id="available-players" aria-labelledby="available-players-tab" className="roster-available-body">
          {/* Sidebar Header */}
          <div className="roster-player-filters">

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={`Search ${playerName.toLowerCase()}s...`}
                aria-label={`Search available ${playerName.toLowerCase()}s`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="player-search"
              />
            </div>

            {/* Quick Assignment Filter Tabs */}
            <div className="flex gap-1 mt-3 p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setAssignmentFilter("all")}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  assignmentFilter === "all"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                All ({registrations.length})
              </button>
              <button
                onClick={() => setAssignmentFilter("unassigned")}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  assignmentFilter === "unassigned"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Unassigned ({registrations.filter(r => !r.team_id).length})
              </button>
              <button
                onClick={() => setAssignmentFilter("assigned")}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  assignmentFilter === "assigned"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Assigned ({registrations.filter(r => r.team_id).length})
              </button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 justify-between text-slate-600"
              onClick={() => setShowFilters(!showFilters)}
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                More Filters
              </span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-3 space-y-3 pt-3 border-t border-slate-100">
                {/* Payment Status */}
                <div>
                  <Label className="text-xs text-slate-500">Payment Status</Label>
                  <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                    <SelectTrigger className="h-8 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Gender */}
                <div>
                  <Label className="text-xs text-slate-500">Gender</Label>
                  <Select value={genderFilter} onValueChange={setGenderFilter}>
                    <SelectTrigger className="h-8 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Division */}
                {divisions.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500">Division</Label>
                    <Select value={divisionFilter} onValueChange={setDivisionFilter}>
                      <SelectTrigger className="h-8 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Divisions</SelectItem>
                        {divisions.map((division) => (
                          <SelectItem key={division.value} value={division.value}>
                            {division.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selection Actions */}
          {filteredRegistrations.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={selectedPlayers.length === filteredRegistrations.length ? deselectAll : selectAllFiltered}
                >
                  {selectedPlayers.length === filteredRegistrations.length ? (
                    <>
                      <Square className="w-3 h-3 mr-1" />
                      Deselect
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-3 h-3 mr-1" />
                      Select All
                    </>
                  )}
                </Button>
                {selectedPlayers.length > 0 && (
                  <span className="text-xs text-slate-500">{selectedPlayers.length} selected</span>
                )}
              </div>
            </div>
          )}

          {/* Player List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {filteredRegistrations.length > 0 ? (
              filteredRegistrations.map((reg) => (
                <DraggablePlayer
                  key={reg.registration_id}
                  registration={reg}
                  player={reg.player}
                  isSelected={selectedPlayers.includes(reg.registration_id)}
                  onSelect={togglePlayerSelection}
                  isDragging={activeId === reg.registration_id}
                  isPending={pendingAssignments[reg.registration_id] !== undefined}
                  onToggleLock={handleToggleRosterLock}
                  onEditConstraints={openRosterConstraints}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                <Users className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">No {playerName.toLowerCase()}s found</p>
              </div>
            )}
          </div>

          <div className="roster-add-action">
            <p className="roster-selection-count">{selectedPlayers.length} {playerName.toLowerCase()}{selectedPlayers.length !== 1 ? 's' : ''} selected</p>
            <Button disabled={!selectedPlayers.length || !selectedTeam} onClick={() => handleBulkAssign(currentTeamId)}>Add players to {selectedTeam?.name || "team"}</Button>
            <p>Select and add, or drag into the roster.</p>
          </div>
          </div> : <div role="tabpanel" id="available-staff" aria-labelledby="available-staff-tab" className="roster-available-body" data-testid="staff-sidebar">
            <div className="p-3 space-y-3">
              <Input aria-label="Search eligible staff" placeholder="Search staff..." value={staffSearchQuery} onChange={event => setStaffSearchQuery(event.target.value)} />
              <label className="roster-role-label">Role when adding
                <select value={selectedStaffRole} onChange={event => setSelectedStaffRole(event.target.value)} aria-label="Role when adding staff">
                  {STAFF_ROLES.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
                </select>
              </label>

            </div>
            <div className="roster-candidate-list" aria-busy={loadingStaff}>
              {!selectedTeam ? <p>Create a team to see eligible staff.</p> : loadingStaff ? <p role="status">Loading eligible staff…</p> : <>
                {staffError && <p role="alert">{staffError}</p>}
                {!staffError && !filteredStaff.length && <p>No eligible staff found for this team.</p>}
                {filteredStaff.map(person => <DraggableStaff key={staffKey(person)} person={person} teamId={currentTeamId} selected={selectedStaffId === staffKey(person)} onSelect={() => setSelectedStaffId(selectedStaffId === staffKey(person) ? "" : staffKey(person))} disabled={addingStaff} />)}
              </>}
            </div>
            <div className="roster-add-action">
              <p className="roster-selection-count">{selectedStaffId ? '1 staff member selected' : 'No staff selected'}</p>
              <Button disabled={!selectedStaffId || loadingStaff || addingStaff || !selectedTeam} onClick={() => handleAddStaffToTeam(selectedStaffId, currentTeamId)}>{addingStaff ? "Adding staff…" : `Add staff to ${selectedTeam?.name || "team"}`}</Button>
              <p>Staff save immediately, separately from player Undo and publishing.</p>
              <Button variant="ghost" disabled={loadingStaff || addingStaff || !selectedTeam} onClick={fetchAvailableStaff}>Refresh eligible staff</Button>
            </div>
          </div>}
        </section>

        {/* Main Content - Teams */}
        <div className="roster-main flex-1 flex flex-col overflow-hidden">
          {/* Teams Container - Horizontal Scroll */}
          {selectedTeam && viewMode === "board" ? (
            <div className="flex-1 overflow-x-auto pb-4">
              <div className="program-builder-board flex gap-4 h-full" role="region" aria-label="Team board">
                {(selectedTeam ? [selectedTeam] : []).map((team) => (
                  <TeamColumn
                    key={team.team_id}
                    team={team}
                    registrations={teamRegistrations[team.team_id] || []}
                    onRemovePlayer={handleRemoveFromTeam}
                    onEditTeam={onEditTeam}
                    onDeleteTeam={onDeleteTeam}
                    onPublishTeam={() => handleRequestRelease(team)}
                    onUnpublishTeam={onUnpublishTeam}
                    onManageStaff={handleManageTeamStaff}
                    publishingTeam={publishingTeam}
                    isGroupMode={isGroupMode}
                    squadlineStatus={squadlineStatus}
                    onSyncTeam={handleSyncSingleTeam}
                    syncingTeamId={syncingTeamId}
                    onResendPlayerInvite={handleResendPlayerInvite}
                    resendingPlayerInvite={resendingPlayerInvite}
                  />
                ))}
              </div>

              {/* Unassign Drop Zone */}
              <UnassignedDropZone isOver={false} />
            </div>
          ) : selectedTeam ? (
            <div className="flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white" data-testid="roster-table-view">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3">Player</th><th className="px-4 py-3">Division</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Team</th><th className="px-4 py-3">Lock</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRegistrations.map((registration) => (
                    <tr key={registration.registration_id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{registration.player?.first_name} {registration.player?.last_name}</td>
                      <td className="px-4 py-3 text-slate-600">{registration.division_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{registration.status || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{registration.payment_status || "—"}</td>
                      <td className="px-4 py-3">
                        <Select value={registration.team_id || "unassigned"} onValueChange={(teamId) => applyRosterMoves([{ registration_id: registration.registration_id, to_team_id: teamId === "unassigned" ? null : teamId }], "Roster table move").then(() => onRefresh?.()).catch((error) => toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Could not move player"))} disabled={registration.roster_locked}>
                          <SelectTrigger className="h-8 min-w-40"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{visibleTeams.map((team) => <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3"><button type="button" onClick={() => handleToggleRosterLock(registration)} disabled={lockingRegistrationId === registration.registration_id} className="rounded-md p-2 text-slate-600 hover:bg-slate-100" aria-label={registration.roster_locked ? "Unlock roster placement" : "Lock roster placement"}>{registration.roster_locked ? <Lock className="h-4 w-4 text-amber-600" /> : <Unlock className="h-4 w-4" />}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <div className="text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No {entityName}s Yet</h3>
                <p className="text-slate-500 mb-6">
                  Create {entityName.toLowerCase()}s to start organizing {playerName.toLowerCase()}s
                </p>
                <Button
                  onClick={onCreateTeam}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First {entityName}
                </Button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeType === "staff" && activeId ? (
          <DragOverlayStaff coach={availableStaff.find(person => `staff-${staffKey(person)}` === activeId)} />
        ) : activeDragItem ? (
          <DragOverlayPlayer registration={activeDragItem} player={activeDragItem.player} />
        ) : null}
      </DragOverlay>

      <Dialog open={Boolean(constraintsRegistration)} onOpenChange={(open) => { if (!open) setConstraintsRegistration(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Roster constraints</DialogTitle>
            <DialogDescription>Protect important placements and give Auto-Balance clear relationship, team, and position guidance.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <Checkbox id="constraint-lock" checked={constraintsForm.locked} onCheckedChange={(checked) => setConstraintsForm((current) => ({ ...current, locked: checked }))} />
              <div><Label htmlFor="constraint-lock">Lock current placement</Label><p className="text-xs text-amber-800">Manual moves and Auto-Balance will leave this player unchanged.</p></div>
            </div>
            {constraintsForm.locked && <div className="sm:col-span-2"><Label>Lock reason</Label><Input value={constraintsForm.lock_reason} onChange={(event) => setConstraintsForm((current) => ({ ...current, lock_reason: event.target.value }))} placeholder="Coach child, approved accommodation…" /></div>}
            <div><Label>Keep with</Label><Select value={constraintsForm.keep_with_registration_id} onValueChange={(value) => setConstraintsForm((current) => ({ ...current, keep_with_registration_id: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No preference</SelectItem>{registrations.filter((registration) => registration.registration_id !== constraintsRegistration?.registration_id).map((registration) => <SelectItem key={registration.registration_id} value={registration.registration_id}>{registration.player?.first_name} {registration.player?.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Keep separate from</Label><Select value={constraintsForm.avoid_registration_id} onValueChange={(value) => setConstraintsForm((current) => ({ ...current, avoid_registration_id: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No restriction</SelectItem>{registrations.filter((registration) => registration.registration_id !== constraintsRegistration?.registration_id).map((registration) => <SelectItem key={registration.registration_id} value={registration.registration_id}>{registration.player?.first_name} {registration.player?.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Preferred team</Label><Select value={constraintsForm.preferred_team_id} onValueChange={(value) => setConstraintsForm((current) => ({ ...current, preferred_team_id: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No preference</SelectItem>{visibleTeams.map((team) => <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Positions / roles</Label><Input value={constraintsForm.preferred_positions} onChange={(event) => setConstraintsForm((current) => ({ ...current, preferred_positions: event.target.value }))} placeholder="Goalkeeper, defender" /></div>
            <div className="sm:col-span-2"><Label>Administrator notes</Label><Textarea value={constraintsForm.notes} onChange={(event) => setConstraintsForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Carpool, sibling, evaluation, or accommodation details…" rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConstraintsRegistration(null)}>Cancel</Button><Button onClick={handleSaveConstraints} disabled={savingConstraints} className="bg-slate-900 text-white hover:bg-slate-800">{savingConstraints && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save constraints</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(releaseTarget)} onOpenChange={(open) => { if (!open) { setReleaseTarget(null); setReleasePreview(null); } }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review roster release</DialogTitle>
            <DialogDescription>Publishing makes these rosters visible and sends team-assignment notifications. Review the impact before continuing.</DialogDescription>
          </DialogHeader>
          {loadingReleasePreview ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /><span className="ml-2 text-sm text-slate-600">Loading release review…</span></div>
          ) : releasePreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 p-3"><p className="text-xs text-slate-500">Teams</p><p className="text-xl font-semibold">{releasePreview.team_count}</p></div>
                <div className="rounded-xl border border-slate-200 p-3"><p className="text-xs text-slate-500">Notifications</p><p className="text-xl font-semibold">{releasePreview.notification_count}</p></div>
                <div className={`rounded-xl border p-3 ${releasePreview.missing_contact_count ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}><p className="text-xs text-slate-500">Missing contact</p><p className="text-xl font-semibold">{releasePreview.missing_contact_count}</p></div>
              </div>
              {releasePreview.blockers?.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3" role="alert"><p className="text-sm font-semibold text-red-900">Complete these items before release</p><ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-800">{releasePreview.blockers.map((blocker, index) => <li key={`${blocker.team_id}-${index}`}>{blocker.team_name}: {blocker.message}</li>)}</ul></div>
              )}
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {releasePreview.teams.map((team) => (
                  <div key={team.team_id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm">
                    <div><p className="font-medium text-slate-900">{team.team_name}</p><p className="text-xs text-slate-500">{team.player_count}{team.capacity ? ` / ${team.capacity}` : ""} players · {team.has_head_coach ? "Coach assigned" : "Coach missing"}</p></div>
                    <div className="text-right text-xs text-slate-500"><p>{team.notification_count} notifications</p>{team.missing_contact_count > 0 && <p className="text-amber-700">{team.missing_contact_count} missing contact</p>}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReleaseTarget(null); setReleasePreview(null); }}>Cancel</Button>
            <Button onClick={handleConfirmRelease} disabled={!releasePreview?.ready || publishingAll || Boolean(publishingTeam)} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="confirm-roster-release">
              {(publishingAll || publishingTeam) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Publish and notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Balance Modal */}
      <Dialog open={showAutoBalanceModal} onOpenChange={setShowAutoBalanceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Auto-Balance {entityName}s
            </DialogTitle>
            <DialogDescription>
              Automatically distribute {playerName.toLowerCase()}s across {entityName.toLowerCase()}s using your preferred strategy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Balance Strategy */}
            <div className="space-y-2">
              <Label>Balance Strategy</Label>
              <Select value={balanceStrategy} onValueChange={(value) => { setBalanceStrategy(value); setBalancePreview(null); }}>
                <SelectTrigger className="w-full rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span>Even Count</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="age">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span>Balance by Age</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="gender">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-purple-500" />
                      <span>Balance by Gender</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="skill">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <span>Balance by Skill Level</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="mixed">
                    <div className="flex items-center gap-2">
                      <Scale className="w-4 h-4 text-emerald-500" />
                      <span>Mixed (Age + Gender + Skill)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {balanceStrategy === "count" && "Distribute players evenly by count only."}
                {balanceStrategy === "age" && "Balance average age across all teams."}
                {balanceStrategy === "gender" && "Balance gender ratio across all teams."}
                {balanceStrategy === "skill" && "Spread skill levels evenly across teams."}
                {balanceStrategy === "mixed" && "Balance using multiple factors for competitive fairness."}
              </p>
            </div>

            {/* Redistribute All Option */}
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
              <Checkbox
                id="redistribute-all"
                checked={redistributeAll}
                onCheckedChange={(checked) => { setRedistributeAll(checked); setBalancePreview(null); }}
              />
              <div>
                <Label htmlFor="redistribute-all" className="text-amber-900 cursor-pointer">
                  Redistribute all {playerName.toLowerCase()}s
                </Label>
                <p className="text-xs text-amber-700 mt-1">
                  Remove all current assignments and redistribute everyone. 
                  Use this for a complete rebalance.
                </p>
              </div>
            </div>

            {/* Stats Preview */}
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-sm font-medium text-slate-700 mb-2">Current Status</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">{entityName}s:</span>
                  <span className="font-medium">{teams.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Total {playerName}s:</span>
                  <span className="font-medium">{registrations.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Assigned:</span>
                  <span className="font-medium text-emerald-600">
                    {registrations.filter(r => r.team_id).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Unassigned:</span>
                  <span className="font-medium text-amber-600">
                    {registrations.filter(r => !r.team_id).length}
                  </span>
                </div>
              </div>
            </div>

            {balancePreview && (
              <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3" data-testid="balance-preview">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-indigo-950">Proposed roster</p>
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-medium text-indigo-700">{balancePreview.moves.length} moves</span>
                </div>
                {balancePreview.warnings?.length > 0 && (
                  <div className="space-y-1" role="alert">
                    {balancePreview.warnings.map((warning, index) => (
                      <p key={`${warning.code}-${index}`} className="text-xs text-amber-800">{warning.message}</p>
                    ))}
                  </div>
                )}
                <div className="max-h-52 space-y-2 overflow-y-auto">
                  {balancePreview.after.map((team) => {
                    const before = balancePreview.before.find((item) => item.team_id === team.team_id);
                    return (
                      <div key={team.team_id} className="rounded-lg border border-indigo-100 bg-white p-2 text-xs">
                        <div className="flex items-center justify-between font-medium text-slate-800">
                          <span>{team.team_name}</span>
                          <span>{before?.player_count ?? 0} → {team.player_count} players</span>
                        </div>
                        <p className="mt-1 text-slate-500">Avg. age {team.average_age ?? "—"} · Avg. skill {team.average_skill ?? "—"}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-indigo-800">Nothing changes until you apply this preview. Locked placements remain unchanged.</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowAutoBalanceModal(false); setBalancePreview(null); }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={balancePreview ? handleApplyBalance : handlePreviewBalance}
              disabled={isBalancing}
              className="bg-slate-900 rounded-full"
              data-testid="execute-auto-balance-btn"
            >
              {isBalancing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {balancePreview ? "Applying..." : "Previewing..."}
                </>
              ) : (
                <>
                  <Scale className="w-4 h-4 mr-2" />
                  {balancePreview ? "Apply proposal" : `Preview ${entityName.toLowerCase()} balance`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Seats Dialog */}
      <Dialog open={showPurchaseSeatsDialog} onOpenChange={setShowPurchaseSeatsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={SQUADLINE_LOGO} alt="Squadline" className="h-6 w-6" />
              No Squadline Seats Available
            </DialogTitle>
            <DialogDescription>
              You need to purchase more Squadline seats to sync {purchaseSeatsInfo?.teamName || 'this team'}.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  {purchaseSeatsInfo?.message ? (
                    <p className="text-sm text-amber-800">{purchaseSeatsInfo.message}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-amber-800">
                        All {purchaseSeatsInfo?.seatsPurchased || 0} seat{purchaseSeatsInfo?.seatsPurchased !== 1 ? 's' : ''} are in use
                      </p>
                      <p className="text-sm text-amber-700">
                        {purchaseSeatsInfo?.seatsUsed || 0} team{purchaseSeatsInfo?.seatsUsed !== 1 ? 's' : ''} already enabled
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-600 mt-4">
              Go to Settings → Integrations → Squadline to purchase additional team licenses.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPurchaseSeatsDialog(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowPurchaseSeatsDialog(false);
                // Navigate to settings integrations tab
                window.location.href = '/admin/settings?tab=integrations';
              }}
              className="bg-amber-500 hover:bg-amber-600 rounded-full"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Purchase Seats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Staff Manager Dialog */}
      <TeamStaffManager
        team={selectedTeamForStaff}
        token={token}
        onStaffUpdated={() => { onRefresh?.(); fetchAvailableStaff(); }}
        isOpen={showStaffManager}
        onClose={() => {
          setShowStaffManager(false);
          setSelectedTeamForStaff(null);
        }}
      />
    </DndContext>
    </div>
  );
};

export default TeamBuilder;
